/**
 * Fase 3 — Prøvesett steg 4: Teknisk oppsett.
 * SEB-browser + spørsmålskilde (bank med smart randomisering vs. statiske spørsmål).
 */
import { Shield, Database, Shuffle, Info } from 'lucide-react';
import type { ExamSetWizardData } from '../NewExamSetWizard';
import SebTerm from '../SebTerm';
import InlineHelpTerm from '../InlineHelpTerm';

export interface TechStepProps {
  data: ExamSetWizardData;
  patch: (updates: Partial<ExamSetWizardData>) => void;
}

export default function TechStep({ data, patch }: TechStepProps) {
  const nonWritten = data.primary_assessment_type !== 'written';

  const updateDifficulty = (key: 'easy' | 'medium' | 'hard', value: number) => {
    const current = data.difficulty_distribution;
    const newValue = value / 100;
    const otherKeys = (['easy', 'medium', 'hard'] as const).filter((k) => k !== key);
    const remaining = 1 - newValue;
    const otherSum = otherKeys.reduce((s, k) => s + current[k], 0);
    const normalized = otherKeys.reduce(
      (acc, k) => {
        acc[k] = otherSum > 0 ? remaining * (current[k] / otherSum) : remaining / 2;
        return acc;
      },
      {} as Record<string, number>,
    );
    patch({
      difficulty_distribution: {
        easy: key === 'easy' ? newValue : normalized.easy,
        medium: key === 'medium' ? newValue : normalized.medium,
        hard: key === 'hard' ? newValue : normalized.hard,
      },
    });
  };

  return (
    <div className="space-y-5">
      {nonWritten && (
        <div className="rounded-lg bg-purple-50 border border-purple-200 p-4 text-sm text-purple-900">
          <p className="font-semibold mb-1">Teknisk oppsett er delvis irrelevant for valgt vurderingsform</p>
          <p>
            <SebTerm /> brukes kun for skriftlige prøver. Spørsmålsbank gjelder bare hvis prøven inneholder spørsmål.
            Du kan hoppe videre uten endringer.
          </p>
        </div>
      )}

      {/* SEB-toggle */}
      <div className="rounded-lg border border-kf-gray-200 bg-kf-gray-50 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Shield size={20} className="text-kf-blue-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <label htmlFor="seb-toggle" className="block text-sm font-medium text-kf-gray-900">
                Krev <SebTerm />
              </label>
              <p className="text-xs text-kf-gray-600 mt-1">
                <strong>Standard: PÅ.</strong> <SebTerm variant="bare" /> låser kandidatens maskin under prøven og forhindrer juks ved å
                blokkere andre applikasjoner, kopiering, nettlesere, screen-sharing-verktøy og tastatursnarveier. Dette
                er en etablert standard for digitale sertifiseringsprøver og gir attesten tyngde ved verifisering.
              </p>
            </div>
          </div>
          <button
            id="seb-toggle"
            type="button"
            role="switch"
            aria-checked={data.seb_required}
            onClick={() => patch({ seb_required: !data.seb_required })}
            disabled={nonWritten}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:opacity-40 disabled:cursor-not-allowed ${
              data.seb_required ? 'bg-kf-blue-600' : 'bg-kf-gray-300'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                data.seb_required ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Spørsmålskilde */}
      <div className="rounded-lg border border-kf-gray-200 p-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Database size={20} className={data.use_question_bank ? 'text-purple-600' : 'text-kf-gray-400'} />
            <div className="min-w-0">
              <label htmlFor="bank-toggle" className="block text-sm font-medium text-kf-gray-900">
                Bruk{' '}
                <InlineHelpTerm
                  slug="wizard-question-bank-randomization"
                  label="spørsmålsbank med smart randomisering"
                  route="/courses-and-exams"
                />
              </label>
              <p className="text-xs text-kf-gray-600 mt-1">
                <strong>Standard: PÅ (anbefalt).</strong> Spørsmål trekkes fra en bank med vanskelighetsfordeling.
                Innen samme kursgjennomføring får alle kandidater samme sett spørsmål, men ulike kurs får ulike utvalg.
                Dette hindrer fasit-deling mellom kurs og gjør det enklere å oppdatere spørsmål uten å endre prøvesettet.
              </p>
            </div>
          </div>
          <button
            id="bank-toggle"
            type="button"
            role="switch"
            aria-checked={data.use_question_bank}
            onClick={() => patch({ use_question_bank: !data.use_question_bank })}
            disabled={nonWritten}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:opacity-40 disabled:cursor-not-allowed ${
              data.use_question_bank ? 'bg-purple-600' : 'bg-kf-gray-300'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                data.use_question_bank ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {!data.use_question_bank && !nonWritten && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Statisk spørsmålssett valgt:</strong> Alle kandidater får samme spørsmål. Dette er enklere å
              administrere, men gir høyere risiko for fasit-deling mellom kull. Bruk kun hvis prøvesettet er lite og
              oppdateres jevnlig.
            </div>
          </div>
        )}

        {/* Bank-konfig (vises når bank er aktivert) */}
        {data.use_question_bank && !nonWritten && (
          <div className="mt-4 space-y-4 border-t border-kf-gray-200 pt-4">
            <div className="flex items-center gap-2 text-purple-800">
              <Shuffle size={16} />
              <span className="text-sm font-medium">Spørsmålsbank-konfigurasjon</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                Antall spørsmål per kandidat
              </label>
              <input
                type="number"
                value={data.question_count}
                onChange={(e) => patch({ question_count: Math.max(1, parseInt(e.target.value) || 1) })}
                min={1}
                max={100}
                className="w-32 px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-kf-gray-700 mb-2">
                Vanskelighetsfordeling
              </label>
              <div className="grid grid-cols-3 gap-3">
                <DifficultySlider
                  label="Enkel"
                  color="green"
                  value={data.difficulty_distribution.easy}
                  onChange={(v) => updateDifficulty('easy', v)}
                />
                <DifficultySlider
                  label="Middels"
                  color="yellow"
                  value={data.difficulty_distribution.medium}
                  onChange={(v) => updateDifficulty('medium', v)}
                />
                <DifficultySlider
                  label="Vanskelig"
                  color="red"
                  value={data.difficulty_distribution.hard}
                  onChange={(v) => updateDifficulty('hard', v)}
                />
              </div>
              <p className="text-xs text-kf-gray-500 mt-2">
                Forhåndsvisning per kandidat: {Math.round(data.question_count * data.difficulty_distribution.easy)} enkle,{' '}
                {Math.round(data.question_count * data.difficulty_distribution.medium)} middels,{' '}
                {Math.round(data.question_count * data.difficulty_distribution.hard)} vanskelige
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface DifficultySliderProps {
  label: string;
  color: 'green' | 'yellow' | 'red';
  value: number;
  onChange: (percent: number) => void;
}

function DifficultySlider({ label, color, value, onChange }: DifficultySliderProps) {
  const colorClasses = {
    green: 'text-green-700 bg-green-200',
    yellow: 'text-yellow-700 bg-yellow-200',
    red: 'text-red-700 bg-red-200',
  }[color];
  return (
    <div>
      <label className={`block text-xs mb-1 ${colorClasses.split(' ')[0]}`}>
        {label} ({Math.round(value * 100)}%)
      </label>
      <input
        type="range"
        min={0}
        max={100}
        value={value * 100}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${colorClasses.split(' ')[1]}`}
      />
    </div>
  );
}
